import { mapStores } from 'pinia';
import { apiClient } from '@/Clients/ApiClient';
import AdminComponentBase from './AdminComponentBase';
import TranslationsAdminService from '@/Services/Admin/TranslationsAdminService';
import { useProjectTranslationsStore } from '@/Stores/Admin/projectTranslationsStore';
import AsyncHelpers from '@/Mixins/AsyncHelpers';
import { isWhitespaceOrEmpty } from '@/Utils/StringUtility';

export default {
    extends: AdminComponentBase,
    mixins: [AsyncHelpers],
    data: () => ({
        translation: null,
        translationList: [],
    }),
    methods: {
        async loadTranslationModel({ entityID, model, formType }) {
            if (!this.isDefaultLanguage) {
                const languageIsoCode = this.projectTranslationsStore.selectedProjectLanguageIsoCode;
                const request = await this.getTranslationModel({
                    projectID: this.projectContext.projectID,
                    languageIsoCode,
                    formType,
                    entityID,
                });

                this.translation = {
                    entityID,
                    formType,
                    languageIsoCode: request.data.languageIsoCode,
                    translationModel: request.data.model,
                    originalModel: model,
                };

                return {
                    ...model, ...Object.entries(this.translation.translationModel)
                        .reduce((acc, [key, val]) => {
                            if (model[key]) {
                                acc[key] = val;
                            }
                            return acc;
                        }, {}),
                };
            }
            else {
                if (this.translation?.originalModel) {
                    model = this.translation.originalModel;
                }
                this.translation = null;
                return model;
            }
        },
        async loadTranslationModelList({ entityIDs, model, formType }, transformFunction = (x) => x) {
            if (!this.isDefaultLanguage) {
                const languageIsoCode = this.projectTranslationsStore.selectedProjectLanguageIsoCode;

                const request = await this.getTranslationModelList({
                    projectID: this.projectContext.projectID,
                    languageIsoCode,
                    formType,
                    entityIDs,
                });

                const translation = {
                    entityIDs,
                    formType,
                    languageIsoCode,
                    translationModelList: request.data,
                    originalModel: model,
                };

                this.translation = translation;

                return transformFunction(translation);
            }
            else {
                if (this.translation?.originalModel) {
                    model = this.translation.originalModel;
                }
                this.translation = null;
                return model;
            }
        },
        async saveTranslationPromise(model) {
            try {
                const request = await this.saveTranslation({
                    projectID: this.projectContext.projectID,
                    entityID: this.translation.entityID,
                    languageIsoCode: this.translation.languageIsoCode,
                    formType: this.translation.formType,
                    content: JSON.stringify(model),
                });

                if (request) {
                    this.hasChanged = false;
                }

                return request;
            }
            catch (error) {
                this.handleError(error);
            }
        },
        async saveTranslationListPromise(model) {
            try {
                const request = await this.saveTranslationList({
                    projectID: this.projectContext.projectID,
                    translations: model,
                });

                this.translationList = [];

                if (request) {
                    this.hasChanged = false;
                }

                return request;
            }
            catch (error) {
                this.handleError(error);
            }
        },
        onBeforeLanguageChange() {
            return this.$validator.validateAll();
        },
        isFieldDisabled(fieldName, ...args) {
            const key = fieldName.replace('model.', '');
            if (this.translation?.translationModel) {
                if (this.translation.originalModel[key] === null || isWhitespaceOrEmpty(this.translation.originalModel[key])) {
                    return true;
                }

                return typeof this.translation.translationModel[key] === 'undefined' || args.includes(true);
            }
            return args.includes(true);
        },
        async loadProjectLanguages(projectId) {
            try {
                if (projectId) {
                    const response = await this.loadProjectLanguagesConfiguration(projectId);
                    if (response.data) {
                        this.projectLanguages = response.data;
                    }
                }
            }
            catch (error) {
                this.handleError(error);
            }
        },
        resetTranslation() {
            this.translation = null;
            this.translationList = [];
            this.projectTranslationsStore.setProjectDefaultLanguage();
        },
    },
    computed: {
        isDefaultLanguage() {
            const selectedProjectLanguage = this.projectTranslationsStore.selectedProjectLanguage;
            if (selectedProjectLanguage) {
                return selectedProjectLanguage.isDefault;
            }

            return true;
        },
        isLanguageSelectorDisabled() {
            return this.isSilentInvalid && this.isDefaultLanguage;
        },
        currentLanguageIsoCode() {
            return this.projectTranslationsStore.selectedProjectLanguageIsoCode;
        },
        translationEntityId() {
            return this.projectContext.projectID;
        },
        hasAvailableLanguages() {
            return this.projectTranslationsStore.hasAvailableLanguages;
        },
        translationPlaceholders() {
            return this.translation?.originalModel;
        },
        ...mapStores(useProjectTranslationsStore),
    },
    created() {
        const translationsAdminService = new TranslationsAdminService(apiClient);
        this.getTranslationModel = this.withProcessing(translationsAdminService
            .getTranslationModel.bind(translationsAdminService));
        this.getTranslationModelList = this.withProcessing(translationsAdminService
            .getTranslationModelList.bind(translationsAdminService));
        this.saveTranslation = this.withProcessing(translationsAdminService
            .saveTranslation.bind(translationsAdminService));
        this.saveTranslationList = this.withProcessing(translationsAdminService
            .saveTranslationList.bind(translationsAdminService));

        this.loadProjectLanguagesConfiguration = this.withProcessing(this.projectTranslationsStore
            .loadProjectLanguagesConfiguration);
    },
    mounted() {
        this.loadProjectLanguages(this.projectContext.projectID);
    },
};
